import { ExtendedChartDataset } from '@app/core/services/charts/chart-data.service';
import { Chart, ChartType, Plugin } from 'chart.js';
import { _DeepPartialObject } from 'node_modules/chart.js/dist/types/utils';

/**
 * @description Configuration of a single set (group) of horizontal threshold lines.
 * Each set produces one horizontal segment per data point, positioned on the value
 * returned by `getValue` and spanning the width of that point's category.
 */
export interface IBackgroundThresholdLineSet {
  /**
   * @description Unique key of the set, used only for debugging/identification.
   */
  id?: string;
  /**
   * @description Reads the value (in Y-axis units) for the segment of a given data point.
   * Return `null` (or a non finite number) to skip the segment for that point.
   * @param meta The `meta` item of the bound dataset for this index.
   * @param index Index of the data point.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getValue: (meta: any, index: number) => number | null;
  /**
   * @description Index of the dataset the `meta` values are read from. Default is 0.
   */
  datasetIndex?: number;
  /**
   * @description Id of the Y axis the values are bound to. Default is 'y'.
   */
  yAxisId?: string;
  color?: string;
  lineWidth?: number;
  lineDash?: number[];
}

export interface IBackgroundThresholdLinesOptions {
  /**
   * @description Default value is true. Set to false to disable all threshold lines.
   */
  enabled?: boolean;
  /**
   * @description Id of the X axis used to resolve category positions. Default is 'x'.
   */
  xAxisId?: string;
  /**
   * @description Sets of horizontal threshold lines to draw.
   */
  sets?: IBackgroundThresholdLineSet[];
}

type IBackgroundThresholdLinesResolvedOptions = _DeepPartialObject<IBackgroundThresholdLinesOptions>;

interface IBackgroundThresholdLinesState {
  draw: (chart: Chart, options: IBackgroundThresholdLinesResolvedOptions) => void;
  enabled: boolean;
}

declare module 'chart.js' {
  interface Chart {
    _backgroundThresholdLinesPlugin?: IBackgroundThresholdLinesState;
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface PluginOptionsByType<TType extends ChartType = ChartType> {
    backgroundThresholdLines?: IBackgroundThresholdLinesOptions;
  }
}

const DEFAULT_LINE_WIDTH = 2;

export const backgroundThresholdLinesPlugin: Plugin = {
  id: 'BackgroundThresholdLines',

  afterInit(chart: Chart) {
    chart._backgroundThresholdLinesPlugin = {
      draw: drawThresholdLines,
      enabled: true,
    };
  },

  beforeDatasetsDraw(chart: Chart) {
    const state = chart._backgroundThresholdLinesPlugin;
    const options = chart.config.options?.plugins?.backgroundThresholdLines;

    if (!state || !options || !(options.enabled ?? true) || !options.sets?.length) {
      return;
    }

    state.enabled = true;
    state.draw(chart, options);
  },

  beforeDestroy(chart: Chart) {
    delete chart._backgroundThresholdLinesPlugin;
  },
};

/**
 * @description Draws every configured set as contiguous horizontal segments on the
 * background of the chart. Each data point gets a segment spanning its category width,
 * placed at the Y pixel matching the value returned by the set's `getValue`.
 */
function drawThresholdLines(chart: Chart, options: IBackgroundThresholdLinesResolvedOptions): void {
  const xScale = chart.scales[options.xAxisId ?? 'x'];
  if (!xScale) return;

  const { top, bottom, left, right, width, height } = chart.chartArea;
  if (width <= 0 || height <= 0) return;

  const ctx = chart.ctx;

  for (const set of options.sets ?? []) {
    if (!set || typeof set.getValue !== 'function') continue;

    const dsIndex = set.datasetIndex ?? 0;
    const dataset = chart.data.datasets?.[dsIndex] as ExtendedChartDataset<unknown> | undefined;
    const meta = chart.getDatasetMeta(dsIndex);
    const metaData = dataset?.meta;

    if (!dataset || !meta || !meta.data?.length || !Array.isArray(metaData)) continue;

    const yScale = chart.scales[set.yAxisId ?? 'y'];
    if (!yScale) continue;

    const points = meta.data;
    const count = Math.min(points.length, metaData.length);

    ctx.save();

    ctx.beginPath();
    ctx.rect(left, top, right - left, bottom - top);
    ctx.clip();

    ctx.lineWidth = set.lineWidth ?? DEFAULT_LINE_WIDTH;
    ctx.strokeStyle = set.color ?? '#000000';
    ctx.setLineDash((set.lineDash as number[]) ?? []);

    for (let i = 0; i < count; i++) {
      const value = set.getValue(metaData[i], i);
      if (value === null || !Number.isFinite(value)) continue;

      const centerX = (points[i] as { x: number }).x;
      if (!Number.isFinite(centerX)) continue;

      // Span the full category width by taking the midpoints to the neighbours.
      const prevX = i > 0 ? (points[i - 1] as { x: number }).x : centerX - (centerX - left);
      const nextX = i < count - 1 ? (points[i + 1] as { x: number }).x : centerX + (right - centerX);

      const segmentLeft = i > 0 ? (prevX + centerX) / 2 : Math.max(left, centerX - (nextX - centerX) / 2);
      const segmentRight = i < count - 1 ? (centerX + nextX) / 2 : Math.min(right, centerX + (centerX - prevX) / 2);

      const y = yScale.getPixelForValue(value);
      if (!Number.isFinite(y)) continue;

      ctx.beginPath();
      ctx.moveTo(segmentLeft, y);
      ctx.lineTo(segmentRight, y);
      ctx.stroke();
    }

    ctx.restore();
  }
}
